import React, { useState } from 'react'
import { Avatar } from '../..'
import classnames from 'classnames'
import TextArea from 'react-textarea-autosize'
import { DirectMessageButton } from './DirectMessageButton'
import { TopicKey } from '../../../constants/customerMessages'
import { QuestionPrequalConfirmationModal } from './QuestionPrequalConfirmationModal'
import { useSafeVerificationFlow } from './hooks'
import { StandardProps as StandardVerificationProps, VerificationProps, AnyQuestionMetadata } from './types'

import styles from './QuestionSingle.module.scss'
import { usePrequalRequest } from '../../../hooks/usePrequalRequest'

// Base props shared by both variants
interface BaseQuestionSingleProps {
  avatarUri?: string
  className?: string
  inputValue: string
  isFocused: boolean
  isLoading: boolean
  messageSentSuccess: boolean | null
  metadata?: AnyQuestionMetadata
  name?: string
  onBlur: (e) => void
  onChange: (e) => void
  onFocus: (e) => void
  onSubmit: (e) => void
  onTextAreaClick: (e) => void
  placeholder?: string
  questionTopicKey: TopicKey
  shouldOpenPrequalConfirmationModalAfterMessageSent?: boolean
}

// Props when verification is required - all verification props must be present
interface VerificationQuestionProps extends BaseQuestionSingleProps, VerificationProps {
  // Override the verifiedQuestionTopicKey to be more specific for this component
  verifiedQuestionTopicKey: TopicKey
}

// Props when verification is not required - verification props must not be present
interface StandardQuestionProps extends BaseQuestionSingleProps, StandardVerificationProps {}

// Discriminated union for component props
type QuestionSingleProps = VerificationQuestionProps | StandardQuestionProps

const QuestionSingle = ({
  className,
  onChange,
  onSubmit,
  inputValue,
  name,
  placeholder,
  avatarUri,
  isLoading,
  isFocused,
  questionTopicKey,
  shouldOpenPrequalConfirmationModalAfterMessageSent,
  onBlur,
  onFocus,
  onTextAreaClick,
  messageSentSuccess,
  metadata,
  // Verification flow props
  requiresPartnerVerification,
  verifiedQuestionTopicKey,
  partnerType
}: QuestionSingleProps) => {
  const { canSendPrequal } = usePrequalRequest()
  const [showPrequalConfirmationModal, setShowPrequalConfirmationModal] = useState(false)

  // Safely access the verification flow context using our safe hook
  const { startVerification, state: verificationState } = useSafeVerificationFlow()

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // If verification is required and we're in the idle state, start verification
    if (requiresPartnerVerification === true && verificationState.currentStep === 'idle') {
      startVerification(inputValue, verifiedQuestionTopicKey, questionTopicKey, partnerType, metadata)
      return
    } else {
      // Otherwise proceed with normal submission
      onSubmit(e)

      if (shouldOpenPrequalConfirmationModalAfterMessageSent && canSendPrequal) {
        setShowPrequalConfirmationModal(true)
      }
    }
  }

  return (
    <div>
      <div
        data-qa='questionSingle'
        className={classnames(className, styles.question, avatarUri && styles.withAvatar, isFocused && styles.focused)}
      >
        <TextArea
          className={styles.textarea}
          value={inputValue}
          name={name}
          placeholder={placeholder}
          aria-label={`dm ${inputValue}`}
          onChange={onChange}
          onClick={onTextAreaClick}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {avatarUri && <Avatar className={styles.avatar} src={avatarUri} />}
        <DirectMessageButton onClick={handleSubmit} loading={isLoading} disabled={!inputValue && !placeholder} />
      </div>
      {showPrequalConfirmationModal && messageSentSuccess && (
        <QuestionPrequalConfirmationModal
          prequalTopicKey={questionTopicKey}
          onClose={() => setShowPrequalConfirmationModal(false)}
        />
      )}
    </div>
  )
}

export default QuestionSingle
